import { z } from 'zod';

/**
 * Multi-tenant dispatch request DTO + form schemas.
 *
 * Powers three role-scoped surfaces:
 *   - supplier: creates / edits / submits / cancels own drafts
 *   - platform: routes submitted requests to a facility + batch
 *   - processor: accepts / rejects / fulfills routed requests
 *
 * Mirrors BE contract `/api/{supplier,platform,facilities,dispatch-requests}/...`.
 */

/**
 * Schema-level messages. This module has no i18n access (it is imported by
 * plain zod resolvers), and zod's own message wins over any localized
 * `setError` that runs later — so these literals reach the user. They are
 * written in Japanese, the app's primary locale, and exported so tests and
 * callers can assert on them instead of copying the string.
 */
export const DISPATCH_REQUESTED_DATE_REQUIRED_MESSAGE = '希望日を選択してください';
export const DISPATCH_REQUESTED_TIME_REQUIRED_MESSAGE = '希望時間を選択してください';
export const DISPATCH_LOCATION_REQUIRED_MESSAGE = '引取場所を入力してください';
export const DISPATCH_FACILITY_REQUIRED_MESSAGE = '処理作業拠点を選択してください';
export const DISPATCH_REJECTION_REASON_REQUIRED_MESSAGE = '却下理由を入力してください';
export const DISPATCH_CANCEL_REASON_REQUIRED_MESSAGE = 'キャンセル理由を入力してください';
export const DISPATCH_RETURN_TO_DRAFT_REASON_REQUIRED_MESSAGE =
  '下書きに戻す理由を入力してください';
export const DISPATCH_INBOUND_LOT_REQUIRED_MESSAGE = '入荷ロット ID を入力してください';
export const DISPATCH_LOCATION_PRESET_REQUIRED_MESSAGE = '場所を入力してください';
/** Mirrors `MAX_STOPS` in the backend dispatch routes (400 beyond that). */
export const DISPATCH_STOPS_MAX = 10;
export const DISPATCH_STOPS_MAX_MESSAGE = `引取場所は${DISPATCH_STOPS_MAX}件までです`;

export const dispatchRequestTypeValues = ['dispatch_out', 'delivery_in'] as const;
export type DispatchRequestType = (typeof dispatchRequestTypeValues)[number];

export const dispatchRequestStatusValues = [
  'draft',
  'submitted',
  'routed',
  'accepted',
  'fulfilled',
  'rejected',
  'cancelled',
] as const;
export type DispatchRequestStatus = (typeof dispatchRequestStatusValues)[number];

/**
 * One pickup address of a request, in `stop_seq` (1-based) order. All stops
 * belong to the same supplier company and carry an address + order only —
 * weight stays one weighing per truck and items stay at receiving.
 */
export const dispatchStopSchema = z.object({
  stop_seq: z.number(),
  location: z.string(),
  notes: z.string().nullable().optional(),
});

export type DispatchStop = z.infer<typeof dispatchStopSchema>;

export const dispatchRejectionSuggestedSlotSchema = z.object({
  date: z.string(),
  start_time: z.string(),
  end_time: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export type DispatchRejectionSuggestedSlot = z.infer<
  typeof dispatchRejectionSuggestedSlotSchema
>;

export const dispatchRequestSchema = z.object({
  dispatch_request_id: z.string(),
  temporary_code: z.string().optional(),
  supplier_company_id: z.string(),
  supplier_user_id: z.string().nullable().optional(),
  routed_facility_id: z.string().nullable().optional(),
  monthly_batch_id: z.string().nullable().optional(),
  linked_inbound_lot_id: z.string().nullable().optional(),
  request_type: z.enum(dispatchRequestTypeValues),
  status: z.enum(dispatchRequestStatusValues),
  requested_date: z.string().nullable().optional(),
  requested_time: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  submitted_at: z.string().nullable().optional(),
  routed_at: z.string().nullable().optional(),
  routed_by: z.string().nullable().optional(),
  accepted_at: z.string().nullable().optional(),
  accepted_by: z.string().nullable().optional(),
  vehicle_number: z.string().nullable().optional(),
  receivable_date: z.string().nullable().optional(),
  cargo_description: z.string().nullable().optional(),
  cargo_quantity: z.string().nullable().optional(),
  contact_name: z.string().nullable().optional(),
  contact_phone: z.string().nullable().optional(),
  rejection_reason: z.string().nullable().optional(),
  return_suggested_availability: z.array(dispatchRejectionSuggestedSlotSchema).optional(),
  // Formal dispatch code, assigned only when the trip is fulfilled.
  trip_code: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  // Every read returns the stop list; `location` above is the denormalized
  // stop 1. Optional so fixtures / older cached reads still type-check.
  stops: z.array(dispatchStopSchema).optional(),
});

export type DispatchRequestDto = z.infer<typeof dispatchRequestSchema>;

export interface DispatchDeleteBlocker {
  code: string;
  count: number;
}

export interface DispatchDeletePreview {
  dispatch_request_id: string;
  linked_inbound_lot_id?: string | null;
  purge_after: string;
  inbound_lot_count: number;
  inbound_package_count: number;
  dismantling_session_count: number;
  dismantling_output_count: number;
  inventory_movement_count: number;
  can_delete: boolean;
  blocker_count: number;
  blockers: DispatchDeleteBlocker[];
  impact_counts?: Record<string, number>;
}

export interface DispatchDeleteResult {
  dispatch_request_id: string;
  linked_inbound_lot_id?: string | null;
  deleted_at: string;
  purge_after: string;
  archive_id: string;
  impact_counts?: Record<string, number>;
}

export interface DeletedDispatch {
  archive_id: string;
  dispatch_request_id: string;
  linked_inbound_lot_id?: string | null;
  monthly_batch_id?: string | null;
  platform_operator_id?: string | null;
  supplier_company_id: string;
  routed_facility_id?: string | null;
  trip_code?: string | null;
  lot_code?: string | null;
  deleted_at: string;
  deleted_by: string;
  deleted_by_display_name: string;
  deleted_by_email: string;
  delete_reason: string;
  purge_after: string;
  recoverable: boolean;
  days_remaining: number;
  purged_at?: string | null;
  restored_at?: string | null;
  impact_counts?: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Supplier-side create / update.
// ---------------------------------------------------------------------------

export const dispatchRequestCreateSchema = z.object({
  supplier_company_id: z.string().optional(),
  request_type: z.enum(dispatchRequestTypeValues),
  requested_date: z.string().min(1, DISPATCH_REQUESTED_DATE_REQUIRED_MESSAGE),
  requested_time: z.string().min(1, DISPATCH_REQUESTED_TIME_REQUIRED_MESSAGE),
  // Kept for compatibility: the BE derives it from `stops[0]`, and the form
  // mirrors stop 1 into it on every edit so the two can never disagree.
  location: z.string().min(1, DISPATCH_LOCATION_REQUIRED_MESSAGE),
  notes: z.string().optional(),
  vehicle_number: z.string().optional(),
  receivable_date: z.string().optional(),
  // What is collected, how much, and who the driver asks for on arrival.
  // Carried into the submit / route notification mails.
  cargo_description: z.string().optional(),
  cargo_quantity: z.string().optional(),
  contact_name: z.string().optional(),
  contact_phone: z.string().optional(),
  stops: z
    .array(
      z.object({
        location: z.string().min(1, DISPATCH_LOCATION_REQUIRED_MESSAGE),
        notes: z.string().optional(),
      }),
    )
    .min(1)
    .max(DISPATCH_STOPS_MAX, DISPATCH_STOPS_MAX_MESSAGE),
});
export type DispatchRequestCreateInput = z.infer<typeof dispatchRequestCreateSchema>;

export const dispatchRequestUpdateSchema = dispatchRequestCreateSchema.partial();
export type DispatchRequestUpdateInput = z.infer<typeof dispatchRequestUpdateSchema> & {
  change_reason?: string;
  notify_supplier?: boolean;
  notify_platform?: boolean;
  notify_processor?: boolean;
  cc_emails?: string[];
};

// ---------------------------------------------------------------------------
// Platform-side route / reject.
// ---------------------------------------------------------------------------

export const dispatchRequestRouteSchema = z.object({
  facility_id: z.string().min(1, DISPATCH_FACILITY_REQUIRED_MESSAGE),
  monthly_batch_id: z.string().optional(),
  cc_emails: z.array(z.string().email()).optional(),
});
export type DispatchRequestRouteInput = z.infer<typeof dispatchRequestRouteSchema>;

export const dispatchRequestRejectSchema = z
  .object({
    reason: z.string().max(2000).optional(),
    suggested_availability: z
      .array(dispatchRejectionSuggestedSlotSchema)
      .max(5)
      .optional(),
    cc_emails: z.array(z.string().email()).optional(),
  })
  .superRefine((value, ctx) => {
    const reasonPresent = !!value.reason?.trim();
    if (!reasonPresent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: DISPATCH_REJECTION_REASON_REQUIRED_MESSAGE,
        path: ['reason'],
      });
    }
  });
export type DispatchRequestRejectInput = z.infer<typeof dispatchRequestRejectSchema>;

export const dispatchRequestCancelSchema = z.object({
  reason: z.string().min(1, DISPATCH_CANCEL_REASON_REQUIRED_MESSAGE),
});
export type DispatchRequestCancelInput = z.infer<typeof dispatchRequestCancelSchema>;

export const dispatchRequestReturnToDraftSchema = z.object({
  reason: z.string().min(1, DISPATCH_RETURN_TO_DRAFT_REASON_REQUIRED_MESSAGE),
});
export type DispatchRequestReturnToDraftInput = z.infer<typeof dispatchRequestReturnToDraftSchema>;

// ---------------------------------------------------------------------------
// Facility-side accept / fulfill.
// ---------------------------------------------------------------------------

export const dispatchRequestAcceptSchema = z.object({
  vehicle_number: z.string().optional(),
  receivable_date: z.string().optional(),
  cc_emails: z.array(z.string().email()).optional(),
});
export type DispatchRequestAcceptInput = z.infer<typeof dispatchRequestAcceptSchema>;

export const dispatchRequestFulfillSchema = z.object({
  inbound_lot_id: z.string().min(1, DISPATCH_INBOUND_LOT_REQUIRED_MESSAGE),
});
export type DispatchRequestFulfillInput = z.infer<typeof dispatchRequestFulfillSchema>;

// ---------------------------------------------------------------------------
// Facility (tenant) DTO — minimal shape for dropdown.
// ---------------------------------------------------------------------------

export interface FacilityDto {
  facility_id: string;
  company_id?: string;
  facility_name?: string;
  company_display_name?: string;
  display_name: string;
}

// ---------------------------------------------------------------------------
// Monthly batch — minimal shape for dropdown (subset of MonthlyBatchDto).
// ---------------------------------------------------------------------------

export interface DispatchMonthlyBatchOption {
  monthly_batch_id: string;
  facility_id: string;
  year_month: string;
  status: string;
}

export const dispatchLocationPresetSchema = z.object({
  dispatch_location_preset_id: z.string(),
  supplier_company_id: z.string(),
  location: z.string(),
  usage_count: z.number(),
  last_used_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type DispatchLocationPreset = z.infer<typeof dispatchLocationPresetSchema>;

export const dispatchLocationPresetCreateSchema = z.object({
  supplier_company_id: z.string().optional(),
  location: z.string().min(1, DISPATCH_LOCATION_PRESET_REQUIRED_MESSAGE),
});

export type DispatchLocationPresetCreateInput = z.infer<typeof dispatchLocationPresetCreateSchema>;

// ---------------------------------------------------------------------------
// Recently accepted vehicles — suggestion list for the facility accept dialog.
// BE dedupes and normalizes per accepting facility, most-recent-first.
// ---------------------------------------------------------------------------

export const dispatchRecentVehicleSchema = z.object({
  vehicle_number: z.string(),
  last_used_at: z.string(),
  use_count: z.number(),
});

export type DispatchRecentVehicle = z.infer<typeof dispatchRecentVehicleSchema>;
